/**
 * The one place the browser talks to the server.
 *
 * Same origin always (the daemon serves this bundle), so: no base URL, no
 * CORS, and the `__Host-` session cookie rides automatically. State-changing
 * requests echo the CSRF cookie in the `x-skyusage-csrf` header — the cookie
 * is readable by design (it is the session cookie that is HttpOnly), and the
 * server compares it against the session's server-side hash.
 */

import type {
  DailyBucket,
  EventPage,
  Me,
  HourTotals,
  ModelTotals,
  RepositoryTotals,
  SessionTotals,
  SourceTotals,
  CostReconciliationRow,
  ProviderBalance,
  QuotaWindow,
  SpendLimit,
  IntegrationHealth,
  CollectorHealth,
  DataQualityIssues,
  BudgetEvaluation,
  RangeParams,
  ReconciliationRow,
  Summary,
  UserTotals,
} from './types'

/** Must match `skyusage_api::session::CSRF_COOKIE` / `CSRF_HEADER`. */
const CSRF_COOKIE = '__Host-skyusage_csrf'
const DEV_CSRF_COOKIE = 'skyusage_dev_csrf'
const CSRF_HEADER = 'x-skyusage-csrf'

/**
 * Every request resolves to one of these; exceptions never escape this
 * module. `unauthenticated` is its own state, not an error: it means "show
 * the login screen", and conflating it with a failure would put a scary red
 * banner on the ordinary logged-out case.
 */
export type ApiResult<T> =
  | { readonly kind: 'ok'; readonly data: T }
  | { readonly kind: 'unauthenticated' }
  | { readonly kind: 'error'; readonly status: number; readonly message: string }

async function get<T>(
  path: string,
  params?: RangeParams & { limit?: number; offset?: number; tz?: string },
): Promise<ApiResult<T>> {
  const query = new URLSearchParams()
  if (params?.from) query.set('from', params.from)
  if (params?.to) query.set('to', params.to)
  if (params?.user) query.set('user', params.user)
  if (params?.limit !== undefined) query.set('limit', String(params.limit))
  if (params?.offset !== undefined) query.set('offset', String(params.offset))
  if (params?.tz) query.set('tz', params.tz)
  const qs = query.size > 0 ? `?${query.toString()}` : ''

  let response: Response
  try {
    response = await fetch(`${path}${qs}`, {
      cache: 'no-store',
      headers: { accept: 'application/json' },
    })
  } catch {
    return { kind: 'error', status: 0, message: 'the server is unreachable' }
  }

  if (response.status === 401) return { kind: 'unauthenticated' }
  if (!response.ok) {
    const message = await response.text().catch(() => '')
    return { kind: 'error', status: response.status, message: message || `HTTP ${response.status}` }
  }
  return { kind: 'ok', data: (await response.json()) as T }
}

function rangeQuery(params: RangeParams): string {
  const query = new URLSearchParams()
  if (params.from) query.set('from', params.from)
  if (params.to) query.set('to', params.to)
  if (params.user) query.set('user', params.user)
  return query.size > 0 ? `?${query.toString()}` : ''
}

function csrfToken(): string | null {
  for (const pair of document.cookie.split(';')) {
    const [name, ...rest] = pair.trim().split('=')
    if (name === CSRF_COOKIE || name === DEV_CSRF_COOKIE) return rest.join('=')
  }
  return null
}

export const api = {
  me: () => get<Me>('/api/v1/auth/me'),
  collectors: () => get<readonly CollectorHealth[]>('/api/v1/collectors'),
  summary: (range: RangeParams) => get<Summary>('/api/v1/usage/summary', range),
  daily: (range: RangeParams) => get<readonly DailyBucket[]>('/api/v1/usage/daily', range),
  byModel: (range: RangeParams) => get<readonly ModelTotals[]>('/api/v1/usage/by-model', range),
  bySource: (range: RangeParams) => get<readonly SourceTotals[]>('/api/v1/usage/by-source', range),
  byHour: (range: RangeParams, tz: string) =>
    get<readonly HourTotals[]>('/api/v1/usage/by-hour', { ...range, tz }),
  byRepository: (range: RangeParams) =>
    get<readonly RepositoryTotals[]>('/api/v1/usage/by-repository', range),
  bySession: (range: RangeParams) =>
    get<readonly SessionTotals[]>('/api/v1/usage/by-session', range),
  byUser: (range: RangeParams) => get<readonly UserTotals[]>('/api/v1/usage/by-user', range),
  events: (range: RangeParams, page: { limit?: number; offset?: number }) =>
    get<EventPage>('/api/v1/usage/events', { ...range, ...page }),
  async exportUsage(
    range: RangeParams,
    format: 'csv' | 'json',
  ): Promise<ApiResult<{ readonly blob: Blob; readonly filename: string }>> {
    let response: Response
    try {
      response = await fetch(`/api/v1/exports/usage.${format}${rangeQuery(range)}`, {
        headers: { accept: format === 'csv' ? 'text/csv' : 'application/json' },
      })
    } catch {
      return { kind: 'error', status: 0, message: 'the server is unreachable' }
    }
    if (response.status === 401) return { kind: 'unauthenticated' }
    if (!response.ok) {
      const message = await response.text().catch(() => '')
      return { kind: 'error', status: response.status, message: message || `HTTP ${response.status}` }
    }
    return {
      kind: 'ok',
      data: {
        blob: await response.blob(),
        filename: `skyusage-usage.${format}`,
      },
    }
  },
  async exportCosts(
    range: RangeParams,
    format: 'csv' | 'json',
  ): Promise<ApiResult<{ readonly blob: Blob; readonly filename: string }>> {
    let response: Response
    try {
      response = await fetch(`/api/v1/exports/costs.${format}${rangeQuery(range)}`, {
        headers: { accept: format === 'csv' ? 'text/csv' : 'application/json' },
      })
    } catch {
      return { kind: 'error', status: 0, message: 'the server is unreachable' }
    }
    if (response.status === 401) return { kind: 'unauthenticated' }
    if (!response.ok) {
      const message = await response.text().catch(() => '')
      return { kind: 'error', status: response.status, message: message || `HTTP ${response.status}` }
    }
    return {
      kind: 'ok',
      data: {
        blob: await response.blob(),
        filename: `skyusage-costs.${format}`,
      },
    }
  },
  reconciliation: (range: RangeParams) =>
    get<readonly ReconciliationRow[]>('/api/v1/usage/reconciliation', range),
  costReconciliation: (range: RangeParams) =>
    get<readonly CostReconciliationRow[]>('/api/v1/usage/cost-reconciliation', range),
  balances: () => get<readonly ProviderBalance[]>('/api/v1/balances'),
  quotaWindows: () => get<readonly QuotaWindow[]>('/api/v1/quota-windows'),
  spendLimits: () => get<readonly SpendLimit[]>('/api/v1/spend-limits'),
  integrations: () => get<readonly IntegrationHealth[]>('/api/v1/integrations'),
  dataQualityIssues: () => get<DataQualityIssues>('/api/v1/data-quality/issues'),
  budgets: () => get<readonly BudgetEvaluation[]>('/api/v1/budgets'),

  async createBudget(input: {
    readonly name: string
    readonly currency: string
    readonly amount: string
    readonly threshold_percent: string
  }): Promise<ApiResult<{ readonly id: string }>> {
    const token = csrfToken()
    if (!token) return { kind: 'error', status: 0, message: 'missing CSRF token; reload and try again' }
    let response: Response
    try {
      response = await fetch('/api/v1/budgets', {
        method: 'POST',
        headers: { [CSRF_HEADER]: token, accept: 'application/json', 'content-type': 'application/json' },
        body: JSON.stringify(input),
      })
    } catch {
      return { kind: 'error', status: 0, message: 'the server is unreachable' }
    }
    if (response.status === 401) return { kind: 'unauthenticated' }
    if (!response.ok) {
      const message = await response.text().catch(() => '')
      return { kind: 'error', status: response.status, message: message || `HTTP ${response.status}` }
    }
    return { kind: 'ok', data: (await response.json()) as { readonly id: string } }
  },

  async disableBudget(id: string): Promise<ApiResult<{ readonly disabled: boolean }>> {
    const token = csrfToken()
    if (!token) return { kind: 'error', status: 0, message: 'missing CSRF token; reload and try again' }
    let response: Response
    try {
      response = await fetch(`/api/v1/budgets/${encodeURIComponent(id)}/disable`, {
        method: 'POST',
        headers: { [CSRF_HEADER]: token, accept: 'application/json' },
      })
    } catch {
      return { kind: 'error', status: 0, message: 'the server is unreachable' }
    }
    if (response.status === 401) return { kind: 'unauthenticated' }
    if (!response.ok) {
      const message = await response.text().catch(() => '')
      return { kind: 'error', status: response.status, message: message || `HTTP ${response.status}` }
    }
    return { kind: 'ok', data: (await response.json()) as { readonly disabled: boolean } }
  },

  async resolveDataQualityIssue(
    issueId: string,
    resolution: string,
  ): Promise<ApiResult<{ resolved: boolean }>> {
    const token = csrfToken()
    if (!token) {
      return { kind: 'error', status: 0, message: 'missing CSRF token; reload and try again' }
    }
    let response: Response
    try {
      response = await fetch(`/api/v1/data-quality/issues/${encodeURIComponent(issueId)}/resolve`, {
        method: 'POST',
        headers: {
          [CSRF_HEADER]: token,
          accept: 'application/json',
          'content-type': 'application/json',
        },
        body: JSON.stringify({ resolution }),
      })
    } catch {
      return { kind: 'error', status: 0, message: 'the server is unreachable' }
    }
    if (response.status === 401) return { kind: 'unauthenticated' }
    if (!response.ok) {
      const message = await response.text().catch(() => '')
      return { kind: 'error', status: response.status, message: message || `HTTP ${response.status}` }
    }
    return { kind: 'ok', data: (await response.json()) as { resolved: boolean } }
  },

  /** Sends the browser to Google. A navigation, not a fetch: the redirect chain must own the tab. */
  login(returnTo: string): void {
    window.location.assign(`/api/v1/auth/login?return_to=${encodeURIComponent(returnTo)}`)
  },

  async logout(): Promise<void> {
    const token = csrfToken()
    await fetch('/api/v1/auth/logout', {
      method: 'POST',
      headers: token ? { [CSRF_HEADER]: token } : {},
    }).catch(() => {
      // The cookies are cleared server-side on any outcome; reloading below
      // lands on the login screen either way.
    })
    window.location.assign('/')
  },

  /**
   * Revoke every session this user holds — the "stolen laptop" kill switch.
   *
   * Unlike {@link logout} the server *requires* the CSRF token here, so a
   * missing token is a real failure the caller must see (it means nothing was
   * revoked), not something to swallow. On success the current session is gone
   * too, so the caller lands back on the login screen.
   */
  async logoutEverywhere(): Promise<ApiResult<{ revoked: number }>> {
    const token = csrfToken()
    if (!token) {
      return { kind: 'error', status: 0, message: 'missing CSRF token; reload and try again' }
    }
    let response: Response
    try {
      response = await fetch('/api/v1/auth/logout-all', {
        method: 'POST',
        headers: { [CSRF_HEADER]: token, accept: 'application/json' },
      })
    } catch {
      return { kind: 'error', status: 0, message: 'the server is unreachable' }
    }
    if (response.status === 401) return { kind: 'unauthenticated' }
    if (!response.ok) {
      const message = await response.text().catch(() => '')
      return { kind: 'error', status: response.status, message: message || `HTTP ${response.status}` }
    }
    return { kind: 'ok', data: (await response.json()) as { revoked: number } }
  },
}
